import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { youtubeEmbed, vimeoEmbed, isVimeo, isUploadedVideo } from '@/lib/video';
import { SPRING_SNAP } from '@/lib/motion';
import type { CardLink } from '@/types/database';

interface VideoModalProps {
  video: CardLink | null;
  onClose: () => void;
}

/** Inline video player — YouTube iframe, Vimeo iframe, or HTML5 video for uploads. */
export function VideoModal({ video, onClose }: VideoModalProps) {
  const open = !!video;
  const yt = youtubeEmbed(video?.value);
  const vm = isVimeo(video?.value) ? vimeoEmbed(video?.value) : null;
  const isUpload = isUploadedVideo(video?.value);

  return (
    <AnimatePresence>
      {open && video && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center px-4 safe-pt safe-pb"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-ink/90 backdrop-blur-xl"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-surface/10 text-bone backdrop-blur-md transition-colors hover:bg-surface/20 safe-pt"
          >
            <X className="h-5 w-5" />
          </button>

          <motion.div
            initial={{ y: 16, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.96 }}
            transition={SPRING_SNAP}
            className="relative w-full max-w-3xl"
          >
            <div className="relative w-full overflow-hidden rounded-xl bg-ink shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]">
              <div className="aspect-video w-full bg-black">
                {yt && (
                  <iframe
                    src={yt}
                    title={video.label || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                  />
                )}
                {!yt && vm && (
                  <iframe
                    src={vm}
                    title={video.label || 'Video'}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                  />
                )}
                {!yt && !vm && isUpload && (
                  <video
                    src={video.value}
                    controls
                    autoPlay
                    playsInline
                    className="h-full w-full"
                  />
                )}
                {!yt && !vm && !isUpload && video.value && (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-bone/80">
                    <p className="text-sm">Can't embed this video.</p>
                    <a
                      href={video.value}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="rounded-full bg-bone px-4 py-2 text-xs font-medium text-ink"
                    >
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>

              {(video.label || video.description) && (
                <div className="border-t border-white/10 px-5 py-3 text-bone">
                  {video.label && (
                    <p className="text-sm font-medium tracking-tight">{video.label}</p>
                  )}
                  {video.description && (
                    <p className="mt-1 text-xs text-bone/70">{video.description}</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
